import React, {
  createElement,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import WebcamLib from 'react-webcam';
import CameraswitchOutlinedIcon from '@mui/icons-material/CameraswitchOutlined';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  WebcamContext,
  WebcamContextProps,
} from 'views/EMR/components/WebcamScanDialog/webcamContext';
import isNil from 'lodash/isNil';
import Flash from 'core/components/Flash';
import {
  useShutterSound,
} from 'views/EMR/hooks/media';
import clsx from 'clsx';
import type {
  WebcamProps,
} from 'react-webcam/dist/react-webcam';

type WebcamInstance = InstanceType<typeof WebcamLib>;
type WebcamWrapperProps = Omit<WebcamProps,
  'audio'
  | 'disablePictureInPicture'
  | 'forceScreenshotSourceSize'
  | 'imageSmoothing'
  | 'mirrored'
  | 'onUserMedia'
  | 'onUserMediaError'
  | 'screenshotFormat'
  | 'screenshotQuality'>
  & Partial<Pick<WebcamProps,
  'audio'
  | 'disablePictureInPicture'
  | 'forceScreenshotSourceSize'
  | 'imageSmoothing'
  | 'mirrored'
  | 'onUserMedia'
  | 'onUserMediaError'
  | 'screenshotFormat'
  | 'screenshotQuality'
>>;

const Webcam = forwardRef<WebcamInstance, WebcamWrapperProps>((props, ref) => (
  createElement(WebcamLib as any, {
    audio: false,
    disablePictureInPicture: false,
    forceScreenshotSourceSize: false,
    imageSmoothing: true,
    mirrored: false,
    onUserMedia: () => undefined,
    onUserMediaError: () => undefined,
    screenshotFormat: 'image/jpeg',
    screenshotQuality: 0.92,
    ...props,
    ref,
  })
));

export default function CaptureStep() {
  const {
    cameraError,
    setCameraError,
    isCameraLoading,
    setIsCameraLoading,
    facingMode,
    setFacingMode,
    photoCaptures,
    setPhotoCaptures,
    multiple,
  } = useContext<WebcamContextProps>(WebcamContext);

  const [idealSize, setIdealSize] = useState<{ width?: number; height?: number }>({});
  const [isFlashing, setIsFlashing] = useState(false);

  const webcamRef = useRef<WebcamInstance | null>(null);
  const playShutterSound = useShutterSound();

  const videoConstraints = useMemo(() => ({
    facingMode,
    ...(idealSize.width ? { width: { ideal: idealSize.width } } : {}),
    ...(idealSize.height ? { height: { ideal: idealSize.height } } : {}),
  }), [facingMode, idealSize]);

  const webcamClassName = useMemo(
    () => (facingMode === 'user'
      ? 'w-full h-full object-cover scale-x-[-1]'
      : 'w-full h-full object-cover'),
    [facingMode],
  );

  const applyWebcamCapabilities = useCallback(() => {
    try {
      const { current } = webcamRef;
      const videoEl = current?.video as HTMLVideoElement | undefined;
      const stream = videoEl?.srcObject as MediaStream | undefined;
      const track = stream?.getVideoTracks?.()[0];
      const caps = track?.getCapabilities?.();
      const settings = track?.getSettings?.();
      const maxWidth = caps?.width?.max ?? settings?.width;
      const maxHeight = caps?.height?.max ?? settings?.height;
      if (maxWidth || maxHeight) {
        setIdealSize({
          width: typeof maxWidth === 'number' ? maxWidth : undefined,
          height: typeof maxHeight === 'number' ? maxHeight : undefined,
        });
      }
    } catch {
      // ignore capability probing errors
    }
    setIsCameraLoading(false);
  }, []);

  const triggerFlash = useCallback(() => {
    setIsFlashing(true);
    setTimeout(() => {
      setIsFlashing(false);
    }, 100);
  }, []);

  const handleCapture = useCallback(() => {
    const video = webcamRef.current?.video as HTMLVideoElement | undefined;
    const isValid = video?.videoWidth && video?.videoHeight;
    if (isValid) {
      const targetAspect = 4 / 3;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const videoAspect = vw / vh;

      // Crop rectangle in source coordinates to match object-fit: cover in a 4:3 box
      let sx = 0; let sy = 0; let sw = vw; let
        sh = vh;

      if (videoAspect > targetAspect) {
      // source is wider -> crop left/right
        sh = vh;
        sw = Math.round(vh * targetAspect);
        sx = Math.round((vw - sw) / 2);
      } else {
      // source is taller -> crop top/bottom
        sw = vw;
        sh = Math.round(vw / targetAspect);
        sy = Math.round((vh - sh) / 2);
      }

      // Choose output size (keep as-cropped, or downscale to reduce file size)
      const outW = sw; // or e.g. 1600
      const outH = sh; // or e.g. 1200

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Mirror so capture matches your preview when using the front camera
      if (facingMode === 'user') {
        ctx.translate(outW, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);

      const src = canvas.toDataURL('image/jpeg');
      if (multiple) {
        setPhotoCaptures((prev) => [src, ...prev]);
      } else {
        setPhotoCaptures([src]);
      }
      triggerFlash();
      playShutterSound();
    }
  }, [facingMode, setPhotoCaptures]);

  const stopCurrentStream = useCallback(() => {
    const videoEl = webcamRef.current?.video as HTMLVideoElement | undefined;
    const stream = videoEl?.srcObject as MediaStream | null | undefined;
    if (!isNil(stream)) {
      stream.getTracks().forEach((t) => t.stop());
    }
    if (!isNil(videoEl)) {
      videoEl.srcObject = null;
    }
  }, [triggerFlash, playShutterSound]);

  const toggleCamera = useCallback(() => {
    setIsCameraLoading(true);
    setCameraError('');

    // Stop old camera before requesting the new one
    stopCurrentStream();

    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }, [setFacingMode, setIsCameraLoading, setCameraError, stopCurrentStream]);

  useEffect(() => () => {
    // Cleanup: stop all video tracks when component unmounts
    stopCurrentStream();
  }, []);

  // Handle spacebar key press for capture
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only trigger on spacebar and if button is not disabled
      if (event.code === 'Space' || event.key === ' ') {
        const isDisabled = isCameraLoading || notEmptyString(cameraError);
        if (!isDisabled) {
          event.preventDefault(); // Prevent page scroll
          handleCapture();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCapture, isCameraLoading, cameraError]);

  return (
    <>
      <div className="flex flex-col gap-1">
        {/* Live Preview: */}
        <div className="relative w-full">
          <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
            <Button
              variant="outlined"
              color="inherit"
              onClick={toggleCamera}
              className="!px-2 !py-1 !h-8 !text-gray-700 !bg-white/70 hover:!bg-white"
            >
              <CameraswitchOutlinedIcon className="!w-5 !h-5" />
            </Button>
          </div>
          <div className="flex justify-center items-center w-full bg-black rounded-lg">
            <div className="w-[85%] ">
              <div className="relative overflow-hidden  shadow-inner aspect-[4/3]">
                <Webcam
                  ref={(ref) => { webcamRef.current = ref; }}
                  playsInline
                  audio={false}
                  videoConstraints={videoConstraints}
                  onUserMedia={applyWebcamCapabilities}
                  onUserMediaError={(err) => {
                    setCameraError(typeof err === 'string' ? err : (err?.message ?? 'Unable to access camera.'));
                    setIsCameraLoading(false);
                  }}
                  className={webcamClassName}
                />
                <Flash isFlashing={isFlashing} />
                <div className="pointer-events-none absolute inset-x-0 bottom-4 flex items-center justify-center">
                  <Button
                    variant="contained"
                    color="primary"
                    type="button"
                    onClick={handleCapture}
                    disabled={isCameraLoading || notEmptyString(cameraError)}
                    className="pointer-events-auto inline-flex items-center justify-center !rounded-full bg-blue-500 hover:bg-blue-600 hover:scale-110 hover:shadow-lg focus:bg-blue-600 active:scale-105 disabled:bg-slate-300 disabled:cursor-not-allowed shadow-md !h-14 !w-14 !min-w-[56px] !max-w-[56px] !p-0 !border-solid !border-4 !border-white transition-all duration-500 ease-out"
                  >
                    <span className="sr-only">Capture</span>
                    <div className="h-6 w-6 rounded-full bg-white" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Instructions: */}
        <div className="text-xs font-medium text-gray-600">
          Fill the frame and tap capture/press spacebar to capture each picture.
        </div>
      </div>
      {/* Create thumbnails preview strip */}
      <div className={clsx('w-full min-w-0 overflow-hidden transition-all duration-300 ease-in-out', {
        'h-0': (photoCaptures?.length ?? 0) === 0,
        'h-[64px]': (photoCaptures?.length ?? 0) > 0,
      })}
      >
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(photoCaptures?.length ?? 0) > 0 && photoCaptures.map((src, idx) => (
            <div key={src} className="relative flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 !w-16 !h-16">
              <MediaPreview
                media={{ url: { value: src }, fileName: `photo-${idx + 1}.png` }}
                thumbnail
                onDelete={() => {
                  setPhotoCaptures((prev) => prev.filter((p) => p !== src));
                }}
                onEdit={([edited]) => {
                  if (!isNil(edited)) {
                    const blobUrl = edited.file ? URL.createObjectURL(edited.file) : null;
                    const next = blobUrl ?? edited.dataUrl ?? src;
                    setPhotoCaptures((prev) => prev.map((p, i) => (i === idx ? next : p)));
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
      {/* )} */}
    </>
  );
}
